/**
 * Admin Service
 *
 * API client for admin dashboard endpoints.
 */

import api from './api';

/**
 * Get comprehensive system status for a specific date.
 *
 * @param {string} checkDate - Date in YYYY-MM-DD format (optional, defaults to today)
 * @returns {Promise<Object>} System status including games, predictions, props, and data quality
 */
export const getSystemStatus = async (checkDate = null) => {
  const params = checkDate ? { check_date: checkDate } : {};
  const response = await api.get('/admin/status', { params });
  return response.data;
};

/**
 * Get status history for the last N days.
 *
 * @param {number} days - Number of days of history (default 7)
 * @returns {Promise<Array>} Array of daily status summaries
 */
export const getStatusHistory = async (days = 7) => {
  const response = await api.get('/admin/status/history', { params: { days } });
  return response.data;
};

/**
 * Get prediction performance metrics.
 *
 * @param {string} sport - Filter by sport (optional)
 * @param {string} startDate - Start date YYYY-MM-DD (optional)
 * @param {string} endDate - End date YYYY-MM-DD (optional)
 * @returns {Promise<Object>} Prediction performance data
 */
export const getPredictionPerformance = async (sport = null, startDate = null, endDate = null) => {
  const params = {};
  if (sport) params.sport = sport;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get('/admin/predictions/performance', { params });
  return response.data;
};

/**
 * Get top picks performance statistics.
 *
 * @param {string} sport - Filter by sport (optional)
 * @param {string} startDate - Start date YYYY-MM-DD (optional)
 * @param {string} endDate - End date YYYY-MM-DD (optional)
 * @returns {Promise<Object>} Top picks stats
 */
export const getTopPicksStats = async (sport = null, startDate = null, endDate = null) => {
  const params = {};
  if (sport) params.sport = sport;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get('/admin/top-picks/stats', { params });
  return response.data;
};

/**
 * Get today's featured pick.
 *
 * @param {string} pickDate - Date YYYY-MM-DD (optional, defaults to today)
 * @returns {Promise<Object>} Featured pick data
 */
export const getFeaturedPick = async (pickDate = null) => {
  const params = {};
  if (pickDate) params.pick_date = pickDate;
  const response = await api.get('/top-picks/featured', { params });
  return response.data;
};

/**
 * Get user statistics.
 *
 * @returns {Promise<Object>} User stats including totals, subscriptions, recent signups
 */
export const getUserStats = async () => {
  const response = await api.get('/admin/users/stats');
  return response.data;
};

/**
 * Every user with last seen, plan and onboarding email sent/opened times.
 *
 * @param {{q?: string, sort?: 'created'|'last_seen'|'email', limit?: number, offset?: number}} params
 */
export const listUsers = async (params = {}) => {
  const response = await api.get('/admin/users', { params });
  return response.data;
};

const adminService = {
  getSystemStatus,
  getStatusHistory,
  getPredictionPerformance,
  getTopPicksStats,
  getFeaturedPick,
  getUserStats,
  listUsers
};

export default adminService;
